<template lang="pug">
//- The base prompt for AI coding agents (code/agent-prompts.ts): the opening
//- prompt with this project's id filled in, and the base prompt downloaded
//- under the filename each tool reads. The bytes come from the CDN, so a
//- failed fetch (no CORS, offline) falls back to opening the file as SKAPI.md.
section.bq-meta.bq-agents
    h2.bq-section-title For AI Agents
    p.bq-agents-intro
        | Download the base prompt for AI coding agents to your project root, then include your project ID in your first prompt:

    .bq-code-option
        span.bq-code-caption prompt
        CopyText.bq-code.bq-agents-code(:value="prompt")
            code
                | My Skapi project ID is: #[span.tok-str(translate="no") "{{ projectId }}"].
                br
                | Build me a #[span.tok-p [describe what you want]].
            span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy

    .bq-row.bq-agent-row(v-for="p in AGENT_PLATFORMS" :key="p.key")
        svg.bq-agent-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
            path(:d="AGENT_LOGOS[p.key]")
        .bq-toggle-label
            span {{ p.name }}
            small.bq-toggle-hint(translate="no") {{ p.filename }}
        a.bq-link(
            @click="download(p)"
            :class="{ disabled: downloading }"
        ) {{ downloading === p.filename ? "[Saving...]" : "[Download]" }}

    p.bq-agents-note The file is the same for every tool; only its name differs. It is not tied to this project: the project id goes in the prompt. #[a.bq-cta-link.bq-hint-link(:href="PROJECT_DOCS.aiAgents" target="_blank" rel="noopener") Learn more]
    br
    //- The MCP server: the administrative side, signed in as the visitor. Any
    //- platform signs in through the browser; the /connect page covers the
    //- rest (no browser, scripts, header-only platforms) with an access token.
    p.bq-agents-intro.bq-agents-mcp
        | To let the agent also manage the project itself (secret keys, tickets, users, records, hosting), add the Skapi MCP server to your platform, choose OAuth and sign in with your Skapi account:

    .bq-code-option
        span.bq-code-caption MCP endpoint
        CopyText.bq-code.bq-agents-code(:value="MCP_BASE_URL")
            code(translate="no") {{ MCP_BASE_URL }}
            span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy

    p.bq-agents-note On a machine with no browser, in a script, or on a platform that only takes an Authorization header, #[a.bq-cta-link(:href="MCP_CONNECT_URL" target="_blank" rel="noopener") sign in from a terminal] to get an access token instead. #[a.bq-cta-link.bq-hint-link(:href="PROJECT_DOCS.mcp" target="_blank" rel="noopener") Learn more]
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import CopyText from "@/components/copyText.vue";
import { PROJECT_DOCS } from "@/code/plan-copy";
import { showToast } from "@/components/toast";
import { AGENT_PLATFORMS, AGENT_PROMPT_URL, agentPrompt, downloadAgentPrompt } from "@/code/agent-prompts";
import type { AgentPlatform } from "@/code/agent-prompts";
import { AGENT_LOGOS } from "@/code/agent-logos";
import { MCP_BASE_URL } from "@/code/mcp_oauth";

/** The MCP server's own sign-in page for terminals (skapi.com/mcp sends there too). */
const MCP_CONNECT_URL = `${MCP_BASE_URL}/connect`;

const props = defineProps<{
    /** The compound project id (service.projectId), what new Skapi(id) takes. */
    projectId: string;
}>();

const prompt = computed(() => agentPrompt(props.projectId));

/** The filename being saved, so the links grey out while one is in flight. */
const downloading = ref("");

async function download(p: AgentPlatform) {
    if (downloading.value) return;
    downloading.value = p.filename;
    try {
        const saved = await downloadAgentPrompt(p.filename);
        if (saved !== p.filename) {
            // A dot-file: the browser dropped the dot, so the visitor adds it back.
            showToast(`Saved as ${saved}. Rename it to ${p.filename}: browsers drop the leading dot.`, "info");
        }
    } catch (err) {
        console.warn("[agentPrompts] could not fetch the prompt", err);
        // Without the bytes the file cannot be renamed: hand over the CDN copy
        // as SKAPI.md and say what to call it.
        window.open(AGENT_PROMPT_URL, "_blank", "noopener");
        showToast(`The file opened as SKAPI.md. Rename it to ${p.filename}.`, "warning");
    } finally {
        downloading.value = "";
    }
}
</script>

<style scoped lang="less">
@import "@/assets/less/bq-list-page.less";

/* The MCP block follows the download rows; give it its own breathing room. */
.bq-agents-mcp {
    margin-top: 1.25rem;
}

.bq-agents-intro,
.bq-agents-note {
    margin: 0;
    font-size: 0.82rem;
    line-height: 1.5;
    color: @muted;
}

.bq-agents-note {
    margin-top: 1rem;
    font-size: 0.72rem;
}

/* The prompt box, the landing page's code sample: the box IS CopyText's root
   span, so a click anywhere in it copies. CopyText's own scoped rules make
   that span inline at (0,2,0); the .bq-agents prefix puts these above them. */
.bq-code-option {
    margin: 0.85rem 0 1.25rem;
}

.bq-code-caption {
    display: block;
    margin-bottom: 0.35rem;
    color: @muted;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.bq-agents .bq-code {
    position: relative;
    display: block;
    max-width: 100%;
    min-width: 0;
    padding: 0;
    border: 1px solid @input-line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    color: @ink;
    font-family: @mono;
    font-size: 0.85rem;
    line-height: 1.5;
    text-align: left;

    &:hover {
        text-decoration: none;
    }

    /* Two short lines: wrap on a phone instead of scrolling, so the whole
       prompt stays readable before it is copied. */
    code {
        display: block;
        max-width: 100%;
        padding: 0.75rem 2.6rem 0.75rem 1rem;
        font: inherit;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }

    &:hover .bq-code-copy {
        color: @pink;
    }
}

.bq-code-copy {
    position: absolute;
    top: 0.6rem;
    right: 0.6rem;
    background: @paper;
    line-height: 1;
    font-size: 1.1rem;
    color: @muted;
    transition: color 0.12s ease;
}

.tok-str {
    color: @success;
}

.tok-p {
    color: @muted;
}

/* logo | name + filename | [Download] */
.bq-agent-row {
    justify-content: flex-start;
    gap: 0.85rem;

    .bq-toggle-label {
        flex: 1;
        min-width: 0;
    }
}

.bq-agent-logo {
    flex-shrink: 0;
    width: 1.4rem;
    height: 1.4rem;
    fill: currentColor;
    color: @ink;
}

.bq-link {
    position: relative;
    left: -2px;
}

@media (max-width: 600px) {
    /* bq-list-page.less wraps .bq-row here; keep the three columns on one line. */
    .bq-agent-row {
        flex-wrap: nowrap;
        gap: 0.75rem;
    }
}
</style>
